import React, { memo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Slider from '@material-ui/core/Slider';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import ExpandMoreIcon from 'components/Icons/ArrowIcon';
import { compareByKey, compareByCount, compareByDays } from 'utils/utility';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      position: '-webkit-sticky',
      position: 'sticky',
      top: 10,
    },
  },
  title: {
    background: theme.palette.background.main,
    boxShadow: theme.shadows[10],
  },
  heading: {
    fontWeight: theme.typography.fontWeightRegular,
  },
}));

const blackFilterList = [
  'Completion',
  'Date created',
  'Date of Maturity',
  'Periodic Rewards Claimed',
  'Periodic Rewards Left',
  'Periodic Rewards Immediately Payable',
];

const sortFunction = {
  Term: compareByDays,
};

function FilterPanel({
  stringTraits,
  numericTraits,
  filterStringTraits,
  setFilterStringTraits,
  filterNumericTraits,
  setFilterNumericTraits,
}) {
  const classes = useStyles();

  const [state, setState] = useState({});

  const handleChange = event => {
    setState({ ...state, [event.target.name]: event.target.checked });

    const currentStringTrait = filterStringTraits.find(trait => trait.name === event.target.id) || {
      name: event.target.id,
      values: [],
    };

    const newFilterStringTrait = { ...currentStringTrait };
    if (event.target.checked) {
      newFilterStringTrait.values.push(event.target.name);
    } else {
      newFilterStringTrait.values = newFilterStringTrait.values.filter(
        item => item !== event.target.name,
      );
    }

    let newFilterStringTraitList = [...filterStringTraits];
    const index = newFilterStringTraitList.findIndex(
      item => item.name === newFilterStringTrait.name,
    );

    if (index >= 0) {
      newFilterStringTraitList.splice(index, 1);
    }
    newFilterStringTraitList.push(newFilterStringTrait);
    newFilterStringTraitList = newFilterStringTraitList.filter(item => item.values.length);
    setFilterStringTraits(newFilterStringTraitList);
  };

  const handleNumericChange = (event, newValue) => {
    if (!event.target.id) {
      return;
    }
    setState(state => ({ ...state, [event.target.id]: newValue }));

    const currentNumericTrait = filterNumericTraits.find(
      trait => trait.name === event.target.id,
    ) || {
      name: event.target.id,
      ranges: [],
    };

    const newFilterNumericTrait = { ...currentNumericTrait };
    newFilterNumericTrait.name = event.target.id;
    newFilterNumericTrait.ranges = [];
    newFilterNumericTrait.ranges.push({
      max: newValue[1],
      min: newValue[0],
    });

    let newFilterNumericTraitList = [...filterNumericTraits];
    const index = newFilterNumericTraitList.findIndex(
      item => item.name === newFilterNumericTrait.name,
    );

    if (index >= 0) {
      newFilterNumericTraitList.splice(index, 1);
    }
    newFilterNumericTraitList.push(newFilterNumericTrait);
    newFilterNumericTraitList = newFilterNumericTraitList.filter(item => item.ranges.length > 0);
    setFilterNumericTraits(newFilterNumericTraitList);
  };

  const resetHandler = name => () => {
    setState(state => ({ ...state, [name]: undefined }));
    let newFilterNumericTraitList = [...filterNumericTraits];
    newFilterNumericTraitList = newFilterNumericTraitList.filter(item => item.name !== name);
    setFilterNumericTraits(newFilterNumericTraitList);
  };

  return (
    <div className={classes.root}>
      {stringTraits.map(item => (
        <Accordion className={classes.title} key={item.key}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>{item.key}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box width={1} overflow='auto' maxHeight={200}>
              {item.counts.sort(sortFunction[item.key] || compareByCount).map((pair, index) => (
                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                  key={`liquidityPair-${index}`}
                  pr={1}>
                  <FormControlLabel
                    key={`${item.key}-${index}`}
                    control={
                      <Checkbox
                        id={item.key}
                        checked={state[pair.value] || false}
                        onChange={handleChange}
                        name={pair.value}
                        color='primary'
                      />
                    }
                    label={pair.value}
                  />
                  <Typography>{pair.count}</Typography>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {numericTraits.sort(compareByKey).map(item =>
        item.value.max > item.value.min && !blackFilterList.includes(item.key) ? (
          <Accordion className={classes.title} key={item.key}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className={classes.heading}>{item.key}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box width={1} pr={1}>
                <Slider
                  min={item.value.min}
                  max={item.value.max}
                  name={item.key}
                  id={item.key}
                  value={state[item.key] || [item.value.min, item.value.max]}
                  step={(item.value.max, item.value.min) <= 1 ? 0.01 : 1}
                  onChange={handleNumericChange}
                />
                <Box display='flex' alignItems='center'>
                  <TextField
                    value={state[item.key]?.[0] || item.value.min}
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>Min</InputAdornment>,
                    }}
                  />
                  <TextField
                    value={state[item.key]?.[1] || item.value.max}
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>Max</InputAdornment>,
                    }}
                  />
                  <Button onClick={resetHandler(item.key)}>Reset</Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ) : null,
      )}
    </div>
  );
}

export default memo(FilterPanel);
